import { useEffect, useMemo, useState } from 'react'
import { apiUrl } from '@/lib/apiBase'
import type { ViewerDestinationSnapshot } from '@/lib/comrades/viewerPayload'
import type {
  Competition,
  ProviderSourceState,
  Split,
  Standing,
  TrackedEntity,
  TrackedRoute,
} from './types'
import type {
  ResultsProviderValue,
  TrackingProviderSnapshot,
} from './providers'

interface WireCompetition {
  id: string
  name: string
  status: string
  starts_at: string | null
}

interface WireStanding {
  rank: number | null
  participant_id: string
  display_name: string
  bib: string | null
  category: string | null
  status: string
  elapsed_time: string | null
  gap: string | null
}

interface WireSplit {
  participant_id: string
  checkpoint_id: string
  checkpoint_name: string
  rank: number | null
  elapsed_time: string | null
  recorded_at: string | null
}

interface WireTrackedEntity {
  id: string
  type: TrackedEntity['type']
  name: string
  coordinate: { latitude: number; longitude: number } | null
  distance_km: number | null
  heading_degrees: number | null
  observed_at: string | null
}

interface WireTrackedRoute {
  id: string
  name: string | null
  coordinates: Array<{ latitude: number; longitude: number }>
  distance_km: number | null
}

export interface DestinationProviderSnapshot {
  source: ViewerDestinationSnapshot['data_sources'][number]
  status: ProviderSourceState['status']
  last_fresh_at: string | null
  error_code?: string | null
  results: {
    competitions: WireCompetition[]
    standings: WireStanding[]
    splits: WireSplit[]
  }
  tracking: {
    entities: WireTrackedEntity[]
    routes: WireTrackedRoute[]
  }
}

type ProviderSnapshotMap = Record<string, DestinationProviderSnapshot>

export function mergeProviderSnapshots(
  previous: ProviderSnapshotMap,
  entries: ReadonlyArray<readonly [string, DestinationProviderSnapshot]>,
): ProviderSnapshotMap {
  return Object.fromEntries(
    entries.map(([sourceKey, incoming]) => {
      const lastGood = previous[sourceKey]
      if (
        incoming.status !== 'error'
        || !lastGood
        || !['ready', 'degraded'].includes(lastGood.status)
      ) {
        return [sourceKey, incoming]
      }

      return [
        sourceKey,
        {
          ...lastGood,
          source: incoming.source,
          status: 'degraded',
          error_code: incoming.error_code ?? 'provider_unavailable',
        } satisfies DestinationProviderSnapshot,
      ]
    }),
  )
}

function providerState(
  sourceKey: string,
  snapshot: DestinationProviderSnapshot | undefined,
  fallbackStatus: string,
  fallbackLastFreshAt: string | null,
): ProviderSourceState {
  const allowed = ['unconfigured', 'loading', 'ready', 'degraded', 'error']
  const status = snapshot?.status ?? (
    fallbackStatus === 'connecting'
      ? 'loading'
      : allowed.includes(fallbackStatus)
      ? fallbackStatus as ProviderSourceState['status']
      : 'unconfigured'
  )
  return {
    sourceKey,
    status,
    lastFreshAt: snapshot?.last_fresh_at ?? fallbackLastFreshAt,
    errorCode: snapshot?.error_code ?? null,
  }
}

function competition(value: WireCompetition): Competition {
  return {
    id: value.id,
    name: value.name,
    status: value.status,
    startsAt: value.starts_at,
  }
}

function standing(value: WireStanding): Standing {
  return {
    rank: value.rank,
    participantId: value.participant_id,
    displayName: value.display_name,
    bib: value.bib,
    category: value.category,
    status: value.status,
    elapsedTime: value.elapsed_time,
    gap: value.gap,
  }
}

function split(value: WireSplit): Split {
  return {
    participantId: value.participant_id,
    checkpointId: value.checkpoint_id,
    checkpointName: value.checkpoint_name,
    rank: value.rank,
    elapsedTime: value.elapsed_time,
    recordedAt: value.recorded_at,
  }
}

function trackedEntity(value: WireTrackedEntity): TrackedEntity {
  return {
    id: value.id,
    type: value.type,
    name: value.name,
    coordinate: value.coordinate,
    distanceKm: value.distance_km,
    headingDegrees: value.heading_degrees,
    observedAt: value.observed_at,
  }
}

function trackedRoute(value: WireTrackedRoute): TrackedRoute {
  return {
    id: value.id,
    name: value.name,
    coordinates: value.coordinates,
    distanceKm: value.distance_km,
  }
}

export function buildDestinationProviderValues(
  destination: ViewerDestinationSnapshot | undefined,
  snapshots: ProviderSnapshotMap,
): {
  results: ResultsProviderValue
  tracking: TrackingProviderSnapshot
} {
  const dataSources = destination?.data_sources ?? []
  const resultSource = dataSources.find((source) =>
    source.capabilities.includes('results.v1'))
  const trackingSource = dataSources.find((source) =>
    source.capabilities.includes('tracking.v1'))

  const competitions: Record<string, Competition[]> = {}
  const standingsBySource: Record<string, Standing[]> = {}
  const splitsBySource: Record<string, Split[]> = {}
  const sources: Record<string, ProviderSourceState> = {}
  const entities: Record<string, TrackedEntity[]> = {}
  const routes: Record<string, TrackedRoute[]> = {}

  for (const source of dataSources) {
    const snapshot = snapshots[source.source_key]
    sources[source.source_key] = providerState(
      source.source_key,
      snapshot,
      source.status,
      source.last_fresh_at,
    )
    competitions[source.source_key] =
      snapshot?.results.competitions.map(competition) ?? []
    standingsBySource[source.source_key] =
      snapshot?.results.standings.map(standing) ?? []
    splitsBySource[source.source_key] =
      snapshot?.results.splits.map(split) ?? []
    entities[source.source_key] =
      snapshot?.tracking.entities.map(trackedEntity) ?? []
    routes[source.source_key] =
      snapshot?.tracking.routes.map(trackedRoute) ?? []
  }

  return {
    results: {
      competitions,
      defaultSourceKey: resultSource?.source_key ?? null,
      standings: (query) => {
        const sourceKey = query.sourceKey ?? resultSource?.source_key ?? ''
        const data = standingsBySource[sourceKey] ?? []
        const filtered = query.category
          ? data.filter((item) => item.category === query.category)
          : data
        return {
          ...(sources[sourceKey] ?? providerState(sourceKey, undefined, 'unconfigured', null)),
          data: typeof query.limit === 'number' ? filtered.slice(0, query.limit) : filtered,
        }
      },
      splits: (query) => {
        const sourceKey = query.sourceKey ?? resultSource?.source_key ?? ''
        const data = (splitsBySource[sourceKey] ?? []).filter((item) =>
          (!query.participantId || item.participantId === query.participantId) &&
          (!query.checkpointId || item.checkpointId === query.checkpointId))
        return {
          ...(sources[sourceKey] ?? providerState(sourceKey, undefined, 'unconfigured', null)),
          data,
        }
      },
    },
    tracking: {
      sources,
      entities,
      routes,
      defaultSourceKey: trackingSource?.source_key ?? null,
    },
  }
}

export function useDestinationProviderValues(
  eventId: string,
  destination: ViewerDestinationSnapshot | undefined,
) {
  const [snapshots, setSnapshots] = useState<ProviderSnapshotMap>({})
  const sourceSignature = JSON.stringify(
    (destination?.data_sources ?? []).map((source) => [
      source.source_key,
      source.adapter_key,
      source.adapter_version,
      source.status,
      source.last_fresh_at,
    ]),
  )

  useEffect(() => {
    const sources = destination?.data_sources ?? []
    if (!eventId || sources.length === 0) {
      setSnapshots({})
      return
    }

    const controller = new AbortController()
    let inFlight = false
    const refresh = async () => {
      if (inFlight) return
      inFlight = true
      try {
        const entries = await Promise.all(
          sources.map(async (source) => {
            try {
              const response = await fetch(
                apiUrl(
                  `public/events/${encodeURIComponent(eventId)}/destination/data-sources/${encodeURIComponent(source.source_key)}`,
                ),
                {
                  credentials: 'omit',
                  headers: { Accept: 'application/json' },
                  signal: controller.signal,
                },
              )
              if (!response.ok) {
                throw new Error(`Provider ${source.source_key} returned ${response.status}`)
              }
              const body = await response.json() as { data: DestinationProviderSnapshot }
              return [source.source_key, body.data] as const
            } catch {
              return [
                source.source_key,
                {
                  source,
                  status: 'error',
                  last_fresh_at: source.last_fresh_at,
                  error_code: 'provider_unavailable',
                  results: { competitions: [], standings: [], splits: [] },
                  tracking: { entities: [], routes: [] },
                } satisfies DestinationProviderSnapshot,
              ] as const
            }
          }),
        )
        if (!controller.signal.aborted) {
          setSnapshots((previous) => mergeProviderSnapshots(previous, entries))
        }
      } finally {
        inFlight = false
      }
    }
    void refresh()
    const interval = window.setInterval(() => void refresh(), 15_000)

    return () => {
      controller.abort()
      window.clearInterval(interval)
    }
  // sourceSignature deliberately captures only fields that change the fetch contract.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, sourceSignature])

  return useMemo(
    () => buildDestinationProviderValues(destination, snapshots),
    [destination, snapshots],
  )
}
